import { inject, Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { DateTimeService } from '@application/data-access';
import { LocalStorageFacade } from '@application/localstorage';
import {
  PickupRequestRealtimeDto,
} from '../entities/pickup-request.model';

export type LivePickupHubStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

class AggressiveReconnectPolicy implements signalR.IRetryPolicy {
  private readonly maxDelayMs = 60000;
  private readonly baseDelayMs = 500;

  public nextRetryDelayInMilliseconds(
    retryContext: signalR.RetryContext,
  ): number {
    if (retryContext.previousRetryCount === 0) return 0;
    const attempt = retryContext.previousRetryCount - 1;
    const delay = this.baseDelayMs * Math.pow(2, attempt);
    return Math.min(delay, this.maxDelayMs);
  }
}

@Injectable({ providedIn: 'root' })
export class LivePickupRealtimeService implements OnDestroy {
  private readonly appConfig = inject(APP_CONFIG);
  private readonly apiBase = this.appConfig.apiUrl.access_flow_service;
  private readonly localStorageService = inject(LocalStorageFacade);
  private readonly dateTimeService = inject(DateTimeService);

  private connection: HubConnection | null = null;
  private isStarting = false;
  /**
   * SignalR drops server-side group membership on reconnect, so remember the
   * last group joined and re-join it in `onreconnected` — otherwise the panel
   * goes silent after any reconnect until the user re-navigates.
   */
  private lastJoin: { tenantId: string; accessPointId?: string } | null = null;

  private readonly hubStatusSubject = new BehaviorSubject<LivePickupHubStatus>(
    'disconnected',
  );
  public readonly hubStatus$: Observable<LivePickupHubStatus> =
    this.hubStatusSubject.asObservable();

  private readonly pickupCreatedSubject = new Subject<
    PickupRequestRealtimeDto[]
  >();
  public readonly pickupCreated$ = this.pickupCreatedSubject.asObservable();

  private readonly pickupUpdatedSubject =
    new Subject<PickupRequestRealtimeDto>();
  public readonly pickupUpdated$ = this.pickupUpdatedSubject.asObservable();

  public async start(): Promise<void> {
    if (
      this.isStarting ||
      this.connection?.state === HubConnectionState.Connected ||
      this.connection?.state === HubConnectionState.Connecting
    ) {
      return;
    }

    this.isStarting = true;
    this.emitStatus('connecting');

    try {
      if (this.connection) {
        await this.stop();
      }

      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(`${this.apiBase}/hubs/flow-realtime`, {
          withCredentials: true,
          transport: HttpTransportType.WebSockets,
          skipNegotiation: true,
          accessTokenFactory: () => this.readToken(),
        })
        .configureLogging(LogLevel.Information)
        .withAutomaticReconnect(new AggressiveReconnectPolicy())
        .build();

      this.setupHandlers();

      await this.connection.start();
      this.emitStatus('connected');
    } catch (error) {
      this.emitStatus('disconnected');
      throw error;
    } finally {
      this.isStarting = false;
    }
  }

  public async stop(): Promise<void> {
    this.emitStatus('disconnected');
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch {
        // ignore
      }
      this.connection = null;
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  public async joinTenant(tenantId: string): Promise<void> {
    this.lastJoin = { tenantId };
    await this.invoke('JoinTenant', tenantId);
  }

  public async leaveTenant(tenantId: string): Promise<void> {
    this.lastJoin = null;
    await this.invoke('LeaveTenant', tenantId);
  }

  public async joinAccessPoint(
    tenantId: string,
    accessPointId: string,
  ): Promise<void> {
    this.lastJoin = { tenantId, accessPointId };
    await this.invoke('JoinAccessPoint', tenantId, accessPointId);
  }

  public async leaveAccessPoint(
    tenantId: string,
    accessPointId: string,
  ): Promise<void> {
    this.lastJoin = null;
    await this.invoke('LeaveAccessPoint', tenantId, accessPointId);
  }

  private async rejoinLastGroup(): Promise<void> {
    const last = this.lastJoin;
    if (!last) return;
    try {
      if (last.accessPointId) {
        await this.invoke('JoinAccessPoint', last.tenantId, last.accessPointId);
      } else {
        await this.invoke('JoinTenant', last.tenantId);
      }
    } catch {
      // hubStatus subject already reflects failure
    }
  }

  private async invoke(method: string, ...args: unknown[]): Promise<void> {
    if (this.connection?.state !== HubConnectionState.Connected) return;
    await this.connection.invoke(method, ...args);
  }

  private readToken(): string {
    try {
      const raw = this.localStorageService.getItem('authorization');
      if (!raw) return '';
      const auth = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return auth?.Token ?? '';
    } catch {
      return '';
    }
  }

  /**
   * SignalR payloads are serialized in camelCase (per backend reference) while
   * the rest of the app (OData responses, store, grid templates) expects the
   * PickupRequest fields in PascalCase. Promote each key to PascalCase before
   * emitting downstream so consumers see a single, consistent shape.
   *
   * `??` against the PascalCase form keeps us safe if the backend ever flips
   * the casing — the mapper becomes a no-op rather than overwriting good data.
   */
  private normalizePickupDto(
    dto: Record<string, unknown>,
  ): PickupRequestRealtimeDto {
    const normalized: Record<string, unknown> = { ...dto };
    for (const [key, value] of Object.entries(dto)) {
      if (!key) continue;
      const first = key.charAt(0);
      if (first === first.toLowerCase() && first !== first.toUpperCase()) {
        const pascalKey = first.toUpperCase() + key.slice(1);
        if (normalized[pascalKey] === undefined) {
          normalized[pascalKey] = value;
        }
      }
    }
    return normalized as unknown as PickupRequestRealtimeDto;
  }

  private setupHandlers(): void {
    if (!this.connection) return;

    this.connection.off('pickupCreated');
    this.connection.off('pickupUpdated');

    this.connection.on(
      'pickupCreated',
      (payload: unknown) => {
        const list = Array.isArray(payload) ? payload : [payload];
        const items = list
          .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
          .map((p) => this.dateTimeService.toLocalObject(this.normalizePickupDto(p)));
        if (items.length) this.pickupCreatedSubject.next(items);
      },
    );

    this.connection.on(
      'pickupUpdated',
      (payload: unknown) => {
        if (!payload || typeof payload !== 'object') return;
        this.pickupUpdatedSubject.next(
          this.dateTimeService.toLocalObject(
            this.normalizePickupDto(payload as Record<string, unknown>),
          ),
        );
      },
    );

    this.connection.onclose(() => {
      this.emitStatus('disconnected');
    });

    this.connection.onreconnecting(() => {
      this.emitStatus('reconnecting');
    });

    this.connection.onreconnected(() => {
      this.emitStatus('connected');
      void this.rejoinLastGroup();
    });
  }

  private emitStatus(status: LivePickupHubStatus): void {
    this.hubStatusSubject.next(status);
  }

  ngOnDestroy(): void {
    this.stop();
    this.hubStatusSubject.complete();
    this.pickupCreatedSubject.complete();
    this.pickupUpdatedSubject.complete();
  }
}
